// PAUSAR MENSAJES AUTOMÁTICOS (portado de la agenda, 12-oct-2026). La alternativa a desvincular:
// desvincular apaga TODO y deja el estudio mudo hasta reescanear el QR. La duración es
// OBLIGATORIA: una pausa sin caducidad se olvida y se come mensajes de verdad durante semanas.
import { useCallback, useEffect, useState } from 'react';
import { PauseCircle, PlayCircle, Loader2 } from 'lucide-react';
import { getWaPause, setWaPause, clearWaPause, hastaTexto, type WaPauseState } from '../../services/whatsappApi';

const DURACIONES: { label: string; minutes: number }[] = [
  { label: '1 hora', minutes: 60 },
  { label: '4 horas', minutes: 240 },
  { label: '24 horas', minutes: 1440 },
];

const btnSecundario = 'inline-flex items-center justify-center gap-1.5 rounded-lg border border-line bg-surface px-3.5 py-2 text-sm font-medium text-content-2 transition hover:bg-surface-2 disabled:opacity-50';

export default function WaPauseCard() {
  const [st, setSt] = useState<WaPauseState | null>(null);
  const [level, setLevel] = useState<1 | 2>(2);
  const [minutes, setMinutes] = useState(60);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cargar = useCallback(() => { getWaPause().then(setSt).catch(() => setSt(null)); }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const pausar = async () => {
    setBusy(true); setError(null);
    try { setSt(await setWaPause(level, minutes)); }
    catch (e) { setError(e instanceof Error ? e.message : 'No se pudo pausar'); }
    finally { setBusy(false); }
  };

  const reanudar = async () => {
    setBusy(true); setError(null);
    try { await clearWaPause(); setSt(null); }
    catch (e) { setError(e instanceof Error ? e.message : 'No se pudo reanudar'); }
    finally { setBusy(false); }
  };

  const activa = !!st?.activa;

  return (
    <div className="mt-5 rounded-2xl border border-line bg-surface p-5 shadow-sm" data-testid="wa-pause">
      <div className="flex items-start gap-3">
        <PauseCircle size={20} className={`mt-0.5 shrink-0 ${activa ? 'text-amber-500' : 'text-content-3'}`} />
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-base font-bold text-content">Pausar mensajes automáticos</h2>
          <p className="mt-1 text-sm text-content-2">
            Para cuando estás cargando pagos viejos o cuadrando cuentas y no quieres que a los alumnos les llegue
            nada. <b className="text-content">Lo que mandas tú a mano sigue saliendo</b>, y tu WhatsApp sigue conectado:
            no hace falta desvincular nada.
          </p>
        </div>
      </div>

      {activa ? (
        <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-500/40 dark:bg-amber-500/10">
          <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
            {st?.level === 1 ? 'Campañas y masivos' : 'Todo lo automático'} en pausa {hastaTexto(st?.until ?? null)}
          </p>
          <p className="mt-0.5 text-xs text-amber-800 dark:text-amber-300/90">
            Se reanuda solo cuando se cumpla la hora. Nada de lo pausado se manda después.
          </p>
          <button onClick={reanudar} disabled={busy} className={`mt-3 ${btnSecundario}`}>
            {busy ? <Loader2 size={16} className="animate-spin" /> : <PlayCircle size={16} />} Reanudar ahora
          </button>
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-content-2" htmlFor="wa-pause-level">Qué se calla</label>
            <select id="wa-pause-level" value={level} onChange={(e) => setLevel(Number(e.target.value) === 1 ? 1 : 2)}
              className="w-full rounded-lg border border-line bg-canvas px-3 py-2 text-sm text-content">
              <option value={2}>Todo lo automático (recibos, cumpleaños, campañas…)</option>
              <option value={1}>Solo campañas y avisos masivos</option>
            </select>
          </div>
          <div>
            <p className="mb-1 block text-xs font-semibold text-content-2">Por cuánto tiempo</p>
            <div className="flex gap-2">
              {DURACIONES.map((d) => (
                <button key={d.minutes} onClick={() => setMinutes(d.minutes)}
                  className={`flex-1 rounded-lg border px-2 py-2 text-sm transition ${
                    minutes === d.minutes
                      ? 'border-amber-400 bg-amber-50 font-semibold text-amber-900 dark:bg-amber-500/10 dark:text-amber-200'
                      : 'border-line text-content-2 hover:bg-surface-2'}`}>
                  {d.label}
                </button>
              ))}
            </div>
          </div>
          <button onClick={pausar} disabled={busy} className={`w-full ${btnSecundario}`}>
            {busy ? <Loader2 size={16} className="animate-spin" /> : <PauseCircle size={16} />} Pausar
          </button>
        </div>
      )}

      {error && <p className="mt-2 text-sm text-red-600 dark:text-red-300">{error}</p>}
    </div>
  );
}
